import { memo } from 'react';
import { Box } from '@mui/material';
import { ChartData, ChartOptions } from 'chart.js';
import { tooltipTitle } from 'config/chart-js.config';
import { Bar } from 'react-chartjs-2';

import { KpiDetailsProps } from './kpi-details';
import { formatValueForChart } from './value-formater';

const KpiChartQuartiles: React.FC<KpiDetailsProps> = memo(
  ({ project, scopeKpiValues, kpi, benchmarks, statistics }) => {
    const benchmark = benchmarks?.kpisBenchmarks[kpi.id];

    const facilityId = kpi.dynamicKpiParentName ? `${project.plantId}@${kpi.dynamicKpiParentName}` : project.plantId;
    const scopeValueId =
      kpi.dynamicKpiParentName && kpi.dynamicKpiParentName.length > 0
        ? kpi.id + `@${kpi.dynamicKpiParentName}`
        : kpi.id;

    const dataset = {
      id: kpi.id,
      name: kpi.name,
      projectValue: scopeKpiValues?.[scopeValueId] ?? null,
      benchmarkedFacility: statistics?.get(kpi.id)?.facilityValues[facilityId] ?? null,
      bottomQuartile: benchmark?.bottomQuartile ?? null,
      median: benchmark?.median ?? null,
      topQuartile: benchmark?.topQuartile ?? null,
    };

    const barChartData =
      dataset.projectValue != null
        ? [
            {
              label: ['Benchmarked', 'Facility'],
              value: dataset.benchmarkedFacility,
              color: '#2e96ff',
            },
            { label: ['Bottom', 'Quartile'], value: dataset.bottomQuartile, color: '#02b2af' },
            { label: 'Median', value: dataset.median, color: '#02b2af' },
            { label: 'Top Quartile', value: dataset.topQuartile, color: '#02b2af' },
          ]
        : [];

    if (
      barChartData.length > 0 &&
      dataset.projectValue &&
      benchmark &&
      dataset.projectValue !== dataset.benchmarkedFacility
    ) {
      barChartData.unshift({
        label: ['Current project', '(not the latest)'],
        value: dataset.projectValue,
        color: '#f1ff2e',
      });
    }

    const data: ChartData<'bar'> = {
      labels: barChartData.map((x) => x.label),
      datasets: [
        {
          data: barChartData.map((x) => x.value),
          backgroundColor: barChartData.map((x) => x.color),
        },
      ],
    };

    const options: ChartOptions<'bar'> = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false,
          },
        },
      },
      interaction: {
        intersect: false,
        mode: 'index',
      },
      layout: {
        padding: {
          top: 30,
        },
      },
      plugins: {
        datalabels: {
          display: true,
          anchor: 'end',
          align: 'top',
          color: '#212636',
          formatter: (value) => formatValueForChart(value, kpi, 4),
        },
        legend: {
          display: false,
        },
        tooltip: {
          boxPadding: 5,
          position: 'average',
          callbacks: {
            label: (item) => formatValueForChart(item.parsed.y, kpi, 4),
            title: tooltipTitle,
          },
        },
      },
    };

    if (dataset.projectValue == undefined) {
      return (
        <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', height: 200 }}>
          No data to display
        </Box>
      );
    }

    return <Bar options={options} data={data} />;
  }
);

export default KpiChartQuartiles;
